import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import HistoryLog from '../HistoryLog';
import { EVENT_TIMELINE_ENTITY_TYPES, EVENT_TIMELINE_SUB_TYPES } from 'App/babel/enum/timeline';
import ContactTimelineCreateContact from './ContactTimelineCreateContact';
import { getTimelineRow } from '../../../babel/components/Helpers/getTimelineRow';
import T from 'Components/Helpers/translate';
import ComparisonTypes from 'Resources/ComparisonTypes';
import Contact from 'App/resources/Model/Contact';
import usePartialLoader, { type FetcherFunction } from 'App/components/hooks/usePartialLoader';
import type { HistoryLogRenderedBy } from 'App/components/HistoryLog/ModalHistoryLog';
import { TimelineComponentProps } from 'Components/TimelineRow/TimelineGeneric/TimelineCardConfigs';
import TimelineLoadMore from 'Components/TimelineRow/TimelineLoadMore/TimelineLoadMore';
import Event from 'App/resources/Model/Event';
import HistoryLogSearch from '../HistoryLogSearch';
import { expandHistoryLogSearch } from '../historyLogSearchSynonyms';
import HistoryLogFilterButtons from '../HistoryLogFilterButtons';
import { withMonthHeaders } from '../HistoryLogMonthHeader';
import RequestBuilder from 'Resources/RequestBuilder';
import { makeCancelable } from 'App/babel/helpers/promise';
import { useFeatureAvailable, Feature } from 'App/components/hooks';
import { createExcludedTypesStore } from '../excludedTypesStorage';

const listeners = ['comment.added', 'comment.deleted', 'comment.updated'];

// Mirrors the client card's taxonomy, minus the types that only exist at company
// level (signals, news). Zero-count filters are dropped by
// HistoryLogFilterButtons, so a contact only ever shows the ones it has.
const excludableEntityTypes = {
	activities: ['Activity'],
	appointments: ['Appointment'],
	order: ['Order', 'Agreement'],
	mail: ['Mail'],
	opportunities: ['Opportunity'],
	market: ['Form', 'Manual', 'Visit', 'MarketingCustom', 'Lead', 'SocialEvent', 'Email'],
	tickets: ['Ticket']
} satisfies Record<string, string[]>;

type ExcludableType = keyof typeof excludableEntityTypes;
type HistoryType = ExcludableType | 'all';
type HistoryTypeCounts = { [k in HistoryType]?: number };

const countTypes = ['all', ...(Object.keys(excludableEntityTypes) as ExcludableType[])] as const;

const isExcludableType = (type: HistoryType): type is ExcludableType => type in excludableEntityTypes;

const excludedTypesStore = createExcludedTypesStore(Object.keys(excludableEntityTypes) as ExcludableType[]);

type Props = {
	contact: Contact;
	client: Contact['client'];
	notesFilterActive?: boolean;
	renderedBy: HistoryLogRenderedBy;
	selectButtons?: ReactNode;
	cardAdditionalProps?: Partial<TimelineComponentProps>;
};

const fetcher: FetcherFunction<
	Pick<Props, 'notesFilterActive' | 'contact'> & {
		search?: string;
		excludedTypes?: ExcludableType[];
		focusedType?: ExcludableType | null;
		onlyMetadata?: boolean;
	},
	Event
> = (filters, { contact, notesFilterActive, search, excludedTypes = [], focusedType = null, onlyMetadata = false }) => {
	const event = Tools.Event;

	filters.addFilter(event.attr.contacts.attr?.id, ComparisonTypes.Equals, contact.id);
	filters.addFilter(
		event.attr.date,
		ComparisonTypes.LessThanEquals,
		moment.tz(Tools.userTimezone ?? 'Europe/Stockholm')
	);
	filters.addFilter({ field: 'feedContact2' }, ComparisonTypes.Equals, null);

	if (focusedType) {
		// Asked for positively rather than by excluding the rest, so the feed matches
		// the count on the pill - the types without a pill have nothing to exclude
		// them by.
		filters.addFilter(event.attr.entityType, ComparisonTypes.Equals, excludableEntityTypes[focusedType]);
	} else {
		filters.addFilter(event.attr.entityType, ComparisonTypes.NotEquals, [
			'Contact',
			...excludedTypes.flatMap(type => excludableEntityTypes[type])
		]);
	}

	if (notesFilterActive) {
		filters.addFilter({ field: 'feedNotes' }, ComparisonTypes.Equals, null);
	}

	if (search) {
		filters.addFilter(event.attr.search, ComparisonTypes.Equals, expandHistoryLogSearch(search));
	}

	filters.addSort(event.attr.date, false);
	filters.addSort(event.attr.entityType, false);

	if (onlyMetadata) {
		filters.limit = 0;
	}

	return event.find(filters.build());
};

const singleTypeFetcher: FetcherFunction<
	Pick<Props, 'notesFilterActive' | 'contact'> & { historyType: HistoryType },
	Event
> = (filters, { contact, notesFilterActive, historyType }) => {
	const event = Tools.Event;

	filters.addFilter(event.attr.contacts.attr?.id, ComparisonTypes.Equals, contact.id);
	filters.addFilter(
		event.attr.date,
		ComparisonTypes.LessThanEquals,
		moment.tz(Tools.userTimezone ?? 'Europe/Stockholm')
	);
	filters.addFilter({ field: 'feedContact2' }, ComparisonTypes.Equals, null);

	if (isExcludableType(historyType)) {
		filters.addFilter(event.attr.entityType, ComparisonTypes.Equals, excludableEntityTypes[historyType]);
	} else {
		filters.addFilter(event.attr.entityType, ComparisonTypes.NotEquals, ['Contact']);
	}

	if (notesFilterActive) {
		filters.addFilter({ field: 'feedNotes' }, ComparisonTypes.Equals, null);
	}

	filters.addSort(event.attr.date, false);
	filters.limit = 0;

	return event.find(filters.build());
};

const ContactHistoryLog = ({ contact, client, renderedBy, notesFilterActive, selectButtons }: Props) => {
	const [search, setSearch] = useState('');
	const [excludedTypes, setExcludedTypes] = useState<ExcludableType[]>(excludedTypesStore.read);
	const [focusedType, setFocusedType] = useState<ExcludableType | null>(null);
	const [metadata, setMetadata] = useState<HistoryTypeCounts>({ all: 0 });
	const containerRef = useRef<HTMLDivElement>(null);
	const onSearchChange = useCallback((value: string) => setSearch(value), []);
	const hasCustomerSupport = useFeatureAvailable(Feature.CUSTOMER_SUPPORT);

	const filterKey = `${focusedType}:${excludedTypes.slice().sort().join(',')}`;

	const {
		data: events,
		total: totalEvents,
		loading,
		loadMore,
		reset
	} = usePartialLoader({
		fetcher,
		broadcastTypes: listeners,
		fetcherProps: {
			contact,
			notesFilterActive,
			search,
			excludedTypes,
			focusedType
		}
	});

	const isInitialMount = useRef(true);
	useEffect(() => {
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}
		reset();
	}, [search, filterKey]);

	// One count per filter, so the buttons can show them and hide themselves when
	// the contact has nothing of that kind.
	useEffect(() => {
		const promise = makeCancelable(
			Promise.all(
				countTypes.map(type => {
					if (type === 'tickets' && !hasCustomerSupport) {
						return Promise.resolve({ type, count: 0 });
					}

					return singleTypeFetcher(new RequestBuilder(), {
						contact,
						notesFilterActive,
						historyType: type
					}).then(res => ({ type, count: res.metadata.total }));
				})
			).then(counts => {
				setMetadata(counts.reduce((acc, cur) => ({ ...acc, [cur.type]: cur.count }), {} as HistoryTypeCounts));
			})
		);

		return () => promise.cancel();
	}, [contact.id, notesFilterActive]);

	// Reused when the contact changes, so the focus must not follow the user.
	useEffect(() => {
		setFocusedType(null);
	}, [contact.id]);

	// A pill whose count reaches zero unmounts, taking the control that would clear it.
	useEffect(() => {
		if (focusedType && !metadata[focusedType]) {
			setFocusedType(null);
		}
	}, [metadata, focusedType]);

	// Latch search visibility so clearing the query doesn't unmount the input
	// (which would steal focus) while the timeline refetches.
	const hasShownSearchRef = useRef(false);
	if (totalEvents > 0 || search) {
		hasShownSearchRef.current = true;
	}
	const showSearchInput = hasShownSearchRef.current;

	const filterButtons = useMemo(
		() => [
			{ title: T('all'), value: 'all' as const },
			{ title: T('default.activities'), value: 'activities' as const, icon: 'activity' as const },
			{ title: T('default.appointments'), value: 'appointments' as const, icon: 'calendar' as const },
			{ title: T('default.order'), value: 'order' as const, icon: 'dollar' as const },
			{ title: T('default.mail'), value: 'mail' as const, icon: 'envelope' as const },
			{ title: T('default.opportunities'), value: 'opportunities' as const, icon: 'opportunity' as const },
			{ title: T('default.market'), value: 'market' as const, icon: 'bullhorn' as const },
			...(hasCustomerSupport
				? [{ title: T('default.tickets'), value: 'tickets' as const, icon: 'ticket' as const }]
				: [])
		],
		[hasCustomerSupport]
	);

	if (!contact || !contact.id) {
		return null;
	}
	let timelineElements: ReactNode[] = [];
	if (events && events.length) {
		timelineElements = withMonthHeaders(events, event =>
			event ? (
				<div key={event.id}>{getTimelineRow(event, 'Contact', notesFilterActive, false, renderedBy)}</div>
			) : null
		);
	}

	const contactRegEvent = {
		entityType: EVENT_TIMELINE_ENTITY_TYPES.CONTACT,
		subType: EVENT_TIMELINE_SUB_TYPES.CREATED,
		users: contact.regBy ? [contact.regBy] : [],
		entityId: contact.id,
		client: client,
		contact: contact
	};

	const selectedFilters: HistoryType[] = [
		...(excludedTypes.length === 0 ? (['all'] as const) : []),
		...filterButtons.flatMap(({ value }) =>
			isExcludableType(value) && !excludedTypes.includes(value) ? [value] : []
		)
	];

	// The exclusions are remembered per user, so moving to the next card keeps
	// the timeline the way they left it.
	const updateExcludedTypes = (next: ExcludableType[]) => {
		setExcludedTypes(next);
		excludedTypesStore.write(next);
	};

	const handleFilterChange = (value: HistoryType) => {
		setFocusedType(null);

		if (value === 'all') {
			updateExcludedTypes([]);
			return;
		}

		updateExcludedTypes(
			excludedTypes.includes(value) ? excludedTypes.filter(type => type !== value) : [...excludedTypes, value]
		);
	};

	// Deliberately not stored - this is a look, not a preference, and the next card
	// opens on the full feed again.
	const handleFocusChange = (value: HistoryType) => {
		if (!isExcludableType(value)) {
			return;
		}
		setFocusedType(prev => (prev === value ? null : value));
	};

	return (
		<div ref={containerRef}>
			<HistoryLog
				title={T('history')}
				selectButtons={selectButtons}
				filterButtons={
					<HistoryLogFilterButtons
						containerRef={containerRef}
						buttons={filterButtons}
						selectedFilters={selectedFilters}
						onFilterChange={handleFilterChange}
						focusedFilter={focusedType}
						onFocusChange={renderedBy.modal ? undefined : handleFocusChange}
						metadata={metadata}
						multiSelect
					/>
				}
				searchInput={showSearchInput ? <HistoryLogSearch onSearchChange={onSearchChange} /> : null}
				loading={loading}
			>
				{timelineElements}
				{events.length < totalEvents ? (
					<TimelineLoadMore
						newEventsLoading={loading}
						numEventsLoaded={events.length}
						totalEvents={totalEvents ? totalEvents : null}
						incrementOffset={loadMore}
					/>
				) : null}
				<ContactTimelineCreateContact isHistoryLog event={contactRegEvent} />
			</HistoryLog>
		</div>
	);
};

export default ContactHistoryLog;
